import React, { useEffect, useMemo, useState } from 'react';
import { Edit2, Plus, X } from 'lucide-react';
import { attendanceAPI, userAPI } from '../../services/api';
import { useToast } from '../UIProvider';
import useDialog from '../../hooks/useDialog';
import DatePicker from '../DatePicker';
import Select from '../Select';
import { clinicToday } from '../../utils/clinicTime';
import { datePart } from './attendanceStatus';

// Fixing attendance by hand: correcting an entry's times (a forgotten punch-out the system closed,
// a wrong time) and adding an entry for a day someone was in but never punched. Both need a reason
// and are kept in the entry's correction history.

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

/** "2026-10-02T09:05:00" -> "09:05". */
export const timePart = (value) => (value ? String(value).slice(11, 16) : '');

const TimeField = ({ id, label, value, onChange, disabled }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-semibold mb-1">{label}</label>
    <input
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/[^\d:]/g, '').slice(0, 5))}
      placeholder="HH:MM"
      inputMode="numeric"
      disabled={disabled}
      className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 font-mono"
    />
  </div>
);

/** The problem with a pair of times, or null. */
export const timesProblem = (inTime, outTime) => {
  if (!TIME_RE.test(inTime)) return 'Enter the time in as HH:MM, e.g. 09:05.';
  if (!TIME_RE.test(outTime)) return 'Enter the time out as HH:MM, e.g. 17:30.';
  if (outTime <= inTime) return 'The time out must be after the time in.';
  return null;
};

const Shell = ({ title, icon: Icon, onClose, saving, children, footer, dialogRef }) => (
  <div ref={dialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-label={title}>
    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
      <div className="bg-gradient-to-r from-navy-800 to-navy-700 p-5 flex items-center justify-between rounded-t-2xl">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
            <Icon className="w-4 h-4 text-white" />
          </div>
          <h2 className="text-lg font-bold text-white">{title}</h2>
        </div>
        <button type="button" aria-label="Close" onClick={onClose} disabled={saving} className="p-2 hover:bg-white/20 rounded-lg">
          <X className="w-5 h-5 text-white" />
        </button>
      </div>
      <div className="p-5 space-y-4">{children}</div>
      <div className="px-5 py-3 border-t bg-gray-50 flex justify-end gap-2 rounded-b-2xl">{footer}</div>
    </div>
  </div>
);

export const CorrectEntryModal = ({ entry, onClose, onSaved }) => {
  const notify = useToast();
  const [inTime, setInTime] = useState(timePart(entry.clockInAt));
  const [outTime, setOutTime] = useState(timePart(entry.clockOutAt));
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const dialogRef = useDialog(() => { if (!saving) onClose(); });

  const submit = async () => {
    const problem = timesProblem(inTime, outTime) || (!reason.trim() ? 'Give the reason for the correction.' : null);
    if (problem) { setError(problem); return; }
    setError('');
    setSaving(true);
    try {
      const saved = await attendanceAPI.correct(entry.id, {
        clockInAt: `${entry.workDate}T${inTime}:00`,
        clockOutAt: `${entry.workDate}T${outTime}:00`,
        reason: reason.trim(),
      });
      notify('success', 'Entry corrected');
      onSaved(saved);
    } catch (e) {
      setError(e?.message || 'The correction could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Shell
      title="Correct times"
      icon={Edit2}
      onClose={onClose}
      saving={saving}
      dialogRef={dialogRef}
      footer={(
        <>
          <button type="button" onClick={onClose} disabled={saving} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium">Cancel</button>
          <button type="button" onClick={submit} disabled={saving} className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
            {saving ? 'Saving…' : 'Save correction'}
          </button>
        </>
      )}
    >
      <p className="text-sm text-gray-600">
        <span className="font-medium text-gray-900">{entry.userName}</span>, {datePart(entry.workDate, { weekday: 'short', day: 'numeric', month: 'short' })}
      </p>
      <div className="grid grid-cols-2 gap-3">
        <TimeField id="correct-in" label="Time in" value={inTime} onChange={setInTime} disabled={saving} />
        <TimeField id="correct-out" label="Time out" value={outTime} onChange={setOutTime} disabled={saving} />
      </div>
      <div>
        <label htmlFor="correct-reason" className="block text-sm font-semibold mb-1">Reason <span className="text-red-500">*</span></label>
        <textarea
          id="correct-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          disabled={saving}
          className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
          placeholder="e.g. Forgot to punch out, left at 5:30 pm as usual"
        />
      </div>
      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
    </Shell>
  );
};

export const ManualEntryModal = ({ initialUserId = '', initialDate = '', onClose, onSaved }) => {
  const notify = useToast();
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState(initialUserId);
  const [date, setDate] = useState(initialDate || clinicToday());
  const [inTime, setInTime] = useState('09:00');
  const [outTime, setOutTime] = useState('17:00');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const dialogRef = useDialog(() => { if (!saving) onClose(); });

  useEffect(() => {
    let live = true;
    userAPI.getAll({ status: 'ACTIVE' })
      .then((rows) => {
        if (!live) return;
        const list = Array.isArray(rows) ? rows : (rows?.content || []);
        setUsers(list.sort((a, b) => (a.fullName || '').localeCompare(b.fullName || '')));
      })
      .catch(() => { if (live) setUsers([]); });
    return () => { live = false; };
  }, []);

  const options = useMemo(() => users.map((u) => ({ value: u.id, label: u.fullName || u.username })), [users]);

  const submit = async () => {
    const problem = !userId ? 'Pick the person.'
      : !date ? 'Pick the day.'
        : timesProblem(inTime, outTime) || (!reason.trim() ? 'Give the reason for adding the entry.' : null);
    if (problem) { setError(problem); return; }
    setError('');
    setSaving(true);
    try {
      const saved = await attendanceAPI.createManual({
        userId,
        workDate: date,
        clockInAt: `${date}T${inTime}:00`,
        clockOutAt: `${date}T${outTime}:00`,
        reason: reason.trim(),
      });
      notify('success', 'Entry added');
      onSaved(saved);
    } catch (e) {
      setError(e?.code === 'ENTRY_EXISTS'
        ? 'This person already has an entry that day. Correct it instead.'
        : e?.message || 'The entry could not be added.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Shell
      title="Add entry by hand"
      icon={Plus}
      onClose={onClose}
      saving={saving}
      dialogRef={dialogRef}
      footer={(
        <>
          <button type="button" onClick={onClose} disabled={saving} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium">Cancel</button>
          <button type="button" onClick={submit} disabled={saving} className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
            {saving ? 'Saving…' : 'Add entry'}
          </button>
        </>
      )}
    >
      <p className="text-sm text-gray-600">For a day someone was at the hospital but never punched. It is marked as entered by hand.</p>
      <Select label="Person *" value={userId} onChange={setUserId} options={options} placeholder="Pick the person" disabled={saving} />
      <div>
        <label className="block text-sm font-semibold mb-1">Day <span className="text-red-500">*</span></label>
        <DatePicker value={date} onChange={setDate} max={clinicToday()} disabled={saving} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <TimeField id="manual-in" label="Time in" value={inTime} onChange={setInTime} disabled={saving} />
        <TimeField id="manual-out" label="Time out" value={outTime} onChange={setOutTime} disabled={saving} />
      </div>
      <div>
        <label htmlFor="manual-reason" className="block text-sm font-semibold mb-1">Reason <span className="text-red-500">*</span></label>
        <textarea
          id="manual-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          disabled={saving}
          className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
          placeholder="e.g. Phone was not working, confirmed by the duty doctor"
        />
      </div>
      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
    </Shell>
  );
};
